var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { DocumentRetrievalFailureReason, EpicIntegration, } from "healthex-common/document.retrieval.dtos";
import { isErr, isOk } from "healthex-common/result";
import { asEnum } from "healthex-common/utils";
import { computed, observable, reaction } from "mobx";
import { getHeapGlobalEventProperties } from "../Infra/LoadHeap";
const DOCUMENT_RETRIEVAL_RECORDS_REUSED_EVENT = "DocumentRetrievalRecordsReused";
export class DocumentRetrievalManager {
    get retrieval() {
        return this._retrieval;
    }
    get patientId() {
        var _a;
        return (_a = this.authManager.id) !== null && _a !== void 0 ? _a : "";
    }
    constructor(documentRetrievalService, authManager, analyticsService) {
        this.documentRetrievalService = documentRetrievalService;
        this.authManager = authManager;
        this.analyticsService = analyticsService;
        this.pollingTimerId = null;
        this.pendingRetrievalKeys = new Set();
        this._retrieval = null;
        this.retrievalErrors = [];
        this.checkingForFinalization = false;
        reaction(() => this.retrieval, () => {
            if (this.waitingForFinalization()) {
                this.startPollingTimerIfNeeded();
            }
            else {
                this.clearPollingTimerIfNeeded();
            }
        }, { fireImmediately: true });
    }
    resetRetrieval() {
        this._retrieval = null;
        this.retrievalErrors = [];
        this.pendingRetrievalKeys.clear();
    }
    setRetrievalErrors(errors) {
        this.retrievalErrors = errors;
    }
    async getExistingInProgressDocumentRetrieval() {
        const batch = await this.getExistingRetrieval(undefined, false);
        if (!batch)
            return null;
        await this.updateBatch(batch);
        return batch;
    }
    async initiateDocumentRetrieval(studyId, useTefca) {
        const retrievalKey = `${this.patientId}-${studyId !== null && studyId !== void 0 ? studyId : "null"}`;
        if (this.pendingRetrievalKeys.has(retrievalKey)) {
            return null;
        }
        this.pendingRetrievalKeys.add(retrievalKey);
        let batchResult;
        try {
            batchResult = await this.documentRetrievalService.retrievePatientData(this.patientId, { studyId, useTefca });
        }
        catch (error) {
            console.error(`Error initiating document retrieval: ${error}`);
            this.setTEFCAErrors([DocumentRetrievalFailureReason.GENERIC_ERROR]);
            this.pendingRetrievalKeys.delete(retrievalKey);
            return null;
        }
        let batch = null;
        if (isErr(batchResult)) {
            this.pendingRetrievalKeys.delete(retrievalKey);
            const errorBatch = await this.getExistingInProgressDocumentRetrieval();
            batch = errorBatch;
        }
        else {
            batch = batchResult;
        }
        if (!batch) {
            console.error(`Error initiating document retrieval: No batch found`);
            this.setTEFCAErrors([DocumentRetrievalFailureReason.GENERIC_ERROR]);
            this.pendingRetrievalKeys.delete(retrievalKey);
            return null;
        }
        const tefcaErrors = batch.tefcaQueries
            .filter((query) => query.error)
            .map((query) => {
            var _a, _b, _c;
            const errorStr = (_b = (_a = query.error) === null || _a === void 0 ? void 0 : _a.replace(/^ForbiddenException: /, "")) !== null && _b !== void 0 ? _b : "";
            return (_c = asEnum(DocumentRetrievalFailureReason)(errorStr)) !== null && _c !== void 0 ? _c : DocumentRetrievalFailureReason.GENERIC_ERROR;
        });
        if (tefcaErrors.length > 0) {
            this.setTEFCAErrors(tefcaErrors);
        }
        await this.updateBatch(batch);
        this.pendingRetrievalKeys.delete(retrievalKey);
        return batch.batchId;
    }
    async retryTEFCARetrieval(batchId) {
        const batch = await this.refreshBatch(batchId);
        if (!batch)
            return;
        this.clearTEFCAErrors();
        if (batch.tefcaQueries.some((query) => !query.isComplete)) {
            void this.startTEFCARetrieval(batch.batchId);
        }
    }
    async skipTEFCARetrieval(batchId) {
        const batch = await this.refreshBatch(batchId);
        if (!batch)
            return;
        this.clearTEFCAErrors();
        const updatedBatch = await this.documentRetrievalService.skipTEFCARetrieval(this.patientId, batchId);
        if (isOk(updatedBatch)) {
            await this.updateBatch(updatedBatch);
        }
    }
    async getMostRecentFinalizedTaskBatch(patientId, mustHaveRecords) {
        return await this.documentRetrievalService.getMostRecentFinalizedTaskBatch(patientId, mustHaveRecords);
    }
    async getDownloadPdfLink(batchId) {
        return await this.documentRetrievalService.getTaskBatchDownloadPdfLink(this.patientId, batchId);
    }
    startPollingTimerIfNeeded() {
        this.clearPollingTimerIfNeeded();
        this.pollingTimerId = window.setInterval(() => {
            void this.checkForFinalization();
        }, 3000);
    }
    clearPollingTimerIfNeeded() {
        if (this.pollingTimerId !== null) {
            window.clearInterval(this.pollingTimerId);
            this.pollingTimerId = null;
        }
    }
    clearPollingTimerIfStartedByFastenCallback() {
        this.clearPollingTimerIfNeeded();
    }
    waitingForFinalization() {
        const retrieval = this.retrieval;
        if (!retrieval)
            return false;
        return retrieval.documentSelectionComplete && !retrieval.finalized;
    }
    async refreshBatch(batchId) {
        if (!batchId) {
            console.warn(`Batch id is not valid`);
            return null;
        }
        const batch = await this.documentRetrievalService.getTaskBatch(this.patientId, batchId);
        if (!batch) {
            console.warn(`Batch ${batchId} not found`);
            return null;
        }
        await this.updateBatch(batch);
        return batch;
    }
    async checkForFinalization() {
        if (!this.retrieval)
            return;
        if (this.checkingForFinalization)
            return;
        this.checkingForFinalization = true;
        this.authManager.recordApiActivity();
        try {
            const finalized = await this.documentRetrievalService.getTaskBatchFinalized(this.patientId, this.retrieval.batchId);
            if (finalized) {
                await this.refreshBatch(this.retrieval.batchId);
            }
        }
        finally {
            this.checkingForFinalization = false;
        }
    }
    async getExistingRetrieval(batchId, documentSelectionComplete) {
        return await this.documentRetrievalService.getExistingRetrieval(this.patientId, {
            batchId,
            documentSelectionComplete,
        });
    }
    async updateBatch(batch) {
        var _a;
        if (!batch)
            return;
        if (((_a = this._retrieval) === null || _a === void 0 ? void 0 : _a.batchId) !== batch.batchId) {
            console.log(`[Document Retrieval] New retrieval batch id: ${batch.batchId}`);
            this.clearTEFCAErrors();
        }
        this._retrieval = batch;
    }
    async startTEFCARetrieval(batchId) {
        const batch = await this.documentRetrievalService.startTEFCARetrieval(this.patientId, batchId);
        if (isErr(batch)) {
            this.setTEFCAErrors([batch.error]);
        }
        else {
            await this.updateBatch(batch);
        }
    }
    setTEFCAErrors(errors) {
        this.retrievalErrors = errors;
    }
    clearTEFCAErrors() {
        this.retrievalErrors = [];
    }
    async getFacilitatedFHIRAuthUrl(batchId, fhirEndpoint, redirectUrl, authType, epicIntegration) {
        return await this.documentRetrievalService.getFacilitatedFHIRAuthUrl(this.patientId, batchId, fhirEndpoint, redirectUrl, authType, epicIntegration);
    }
    async getFacilitatedFHIRRecordsForNewLocation(batchId, key, fhirEndpoint, authType, redirectUrl, code) {
        const result = await this.documentRetrievalService.getFacilitatedFHIRRecords(this.patientId, batchId, key, fhirEndpoint, authType, redirectUrl, code, EpicIntegration.OPEN_EPIC, true);
        if (isErr(result))
            return result;
        await this.updateBatch(result);
        return undefined;
    }
    async getFacilitatedFHIRRecords(batchId, locationId, fhirEndpoint, authType, redirectUri, code, epicIntegration) {
        const result = await this.documentRetrievalService.getFacilitatedFHIRRecords(this.patientId, batchId, locationId, fhirEndpoint, authType, redirectUri, code, epicIntegration);
        if (isErr(result))
            return result;
        await this.updateBatch(result);
        return undefined;
    }
    async getSuggestedFastenEndpoints(batchId) {
        return await this.documentRetrievalService.getSuggestedFastenEndpoints(this.patientId, batchId);
    }
    async getFastenDocuments(batchId, connectionId, portalId, brandId, endpointId, source) {
        const batch = await this.documentRetrievalService.trackFastenRetrieval(this.patientId, batchId, connectionId, portalId, brandId, endpointId, source);
        await this.updateBatch(batch);
    }
    async skipLocation(batchId, locationId) {
        const batch = await this.documentRetrievalService.updateTaskBatch(this.patientId, batchId, {
            skipLocationId: locationId,
        });
        await this.updateBatch(batch);
    }
    async updateExcludedLocations(batchId, excludedLocationIds) {
        const batch = await this.documentRetrievalService.updateTaskBatch(this.patientId, batchId, {
            excludedLocationIds,
        });
        await this.updateBatch(batch);
    }
    async doneSelectingDocuments(batchId, studyId, createdBatch) {
        var _a, _b, _c, _d, _e;
        if (((_a = this._retrieval) === null || _a === void 0 ? void 0 : _a.initiatingStudyId) !== studyId) {
            console.warn(`[Document Retrieval] Study ID mismatch: ${(_c = (_b = this._retrieval) === null || _b === void 0 ? void 0 : _b.initiatingStudyId) !== null && _c !== void 0 ? _c : "null"} !== ${studyId}`);
        }
        await this.documentRetrievalService.createDocumentDelivery(this.patientId, {
            batchId,
            initiatingStudyId: (_e = (_d = this._retrieval) === null || _d === void 0 ? void 0 : _d.initiatingStudyId) !== null && _e !== void 0 ? _e : studyId,
            createdBatch,
        });
        const batch = await this.documentRetrievalService.updateTaskBatch(this.patientId, batchId, {
            documentSelectionComplete: true,
        });
        await this.updateBatch(batch);
    }
    async reuseExistingDocuments(existingBatch, studyId) {
        await this.documentRetrievalService.createDocumentDelivery(this.patientId, {
            batchId: existingBatch.batchId,
            initiatingStudyId: studyId,
            createdBatch: null,
        });
        void this.analyticsService.trackHeapEvent({
            event: DOCUMENT_RETRIEVAL_RECORDS_REUSED_EVENT,
            identity: this.patientId,
            properties: {
                ...getHeapGlobalEventProperties(),
            },
        });
    }
    async downloadInitiated(batchId) {
        const batch = await this.documentRetrievalService.updateTaskBatch(this.patientId, batchId, {
            patientHasDownloaded: true,
        });
        await this.updateBatch(batch);
    }
    async getDownloadDocumentsLink(batchId) {
        return await this.documentRetrievalService.getTaskBatchDownloadLink(this.patientId, batchId);
    }
    async getMostRecentDownloadableOrProcessingTaskBatch() {
        return await this.documentRetrievalService.getMostRecentDownloadableOrProcessingTaskBatch(this.patientId);
    }
    async getPatientTEFCAAuthInfo() {
        return await this.documentRetrievalService.getPatientTEFCAAuthInfo(this.patientId);
    }
    async checkIfLocationExists(batchId, input) {
        return await this.documentRetrievalService.checkIfLocationExists(this.patientId, batchId, input);
    }
}
__decorate([
    observable
], DocumentRetrievalManager.prototype, "_retrieval", void 0);
__decorate([
    computed
], DocumentRetrievalManager.prototype, "retrieval", null);
__decorate([
    observable
], DocumentRetrievalManager.prototype, "retrievalErrors", void 0);
__decorate([
    computed
], DocumentRetrievalManager.prototype, "patientId", null);
